/**
 * Pure computation helpers for inbox category data.
 * These are extracted from useInboxContentState to keep the hook within the
 * max-lines-per-function limit. All functions are pure (no hooks or side effects).
 */
import { Email, InboxMode } from 'types/email';

import { CategoryGroup, groupEmailsByCategory } from 'components/inbox/CategoryAccordion';
import { CATEGORY_KEY_PENDING, CATEGORY_OTHER } from 'constants/strings';
import { getCategoryKey } from 'hooks/useEmailFetching';
import { CategorySummaryItem } from 'store/slices/emailSlice';
import { CATEGORY_KEY_UNCATEGORIZED } from 'store/slices/inboxDataSlice';

/**
 * Returns emails in the same display order as the UI renders them:
 * groups are sorted by max priority descending, and emails within each group
 * are sorted by priority descending (matching `groupEmailsByCategory`).
 *
 * Use this instead of the flat server list whenever you need to know which
 * email appears "below" another in the visible inbox.
 */
export function getDisplayOrderedEmails(emails: Email[], mode: InboxMode): Email[] {
  return groupEmailsByCategory(emails, mode).flatMap(group => group.emails);
}

type SplitViewHandle = { openEmail: (id: string) => void; closeEmail: () => void };

/**
 * After an email is removed from the split-view panel (archive, snooze, priority change),
 * navigate to the email that visually replaces it and update the left-panel highlight.
 *
 * Navigation priority:
 * 1. Stay within the same drawer — pick the next email at the same position within
 *    the removed email's category. This avoids jumping to a different drawer when
 *    the user is working through a category one email at a time.
 * 2. If the drawer is now empty, fall back to the next email in the flat display
 *    order across all visible categories.
 *
 * Only considers emails in expanded (visible) categories — collapsed drawers are
 * invisible and should never receive auto-navigation focus.
 */
export function navigateAfterSplitViewAction(
  removedEmailId: string,
  emails: Email[],
  mode: InboxMode,
  splitView: SplitViewHandle,
  setSelectedEmailIndex: (index: number) => void,
  expandedCategories?: Set<string>
): void {
  const picked = pickNextEmailAfterRemoval(removedEmailId, emails, mode, expandedCategories);
  if (!picked) {
    splitView.closeEmail();
    return;
  }
  splitView.openEmail(picked.nextEmailId);
  setSelectedEmailIndex(picked.nextIndexInRemaining);
}

/**
 * Which email visually replaces the removed one, in display order (same
 * drawer-first rules as {@link navigateAfterSplitViewAction}). Returns null
 * when no email remains in any expanded category.
 */
export function pickNextEmailAfterRemoval(
  removedEmailId: string,
  emails: Email[],
  mode: InboxMode,
  expandedCategories?: Set<string>
): { nextEmailId: string; nextIndexInRemaining: number } | null {
  // Include the removed email so we can determine its position before it disappears.
  const activeWithRemoved = emails.filter(email => !email.isArchived || email.id === removedEmailId);

  const groupedEmails = groupEmailsByCategory(activeWithRemoved, mode);
  const visibleGroups = expandedCategories
    ? groupedEmails.filter(group => expandedCategories.has(group.category))
    : groupedEmails;

  const displayOrdered = visibleGroups.flatMap(group => group.emails);
  const remaining = displayOrdered.filter(email => email.id !== removedEmailId);

  if (remaining.length === 0) {
    return null;
  }

  // Find the drawer (category group) that owns the removed email.
  const removedGroup = visibleGroups.find(group =>
    group.emails.some(email => email.id === removedEmailId)
  );
  const siblingsInSameDrawer = removedGroup
    ? removedGroup.emails.filter(email => email.id !== removedEmailId)
    : [];

  let nextEmailId: string;

  if (siblingsInSameDrawer.length > 0) {
    // Stay in the same drawer: use the same position, clamped to the new last index.
    const positionInDrawer = removedGroup!.emails.findIndex(email => email.id === removedEmailId);
    nextEmailId = siblingsInSameDrawer[Math.min(positionInDrawer, siblingsInSameDrawer.length - 1)].id;
  } else {
    // Drawer is now empty — fall back to the next email in the overall display order.
    const removedDisplayIndex = displayOrdered.findIndex(email => email.id === removedEmailId);
    const fallbackIndex = Math.min(
      removedDisplayIndex >= 0 ? removedDisplayIndex : 0,
      remaining.length - 1
    );
    nextEmailId = remaining[fallbackIndex].id;
  }

  const nextIndexInRemaining = remaining.findIndex(email => email.id === nextEmailId);
  return { nextEmailId, nextIndexInRemaining };
}

/**
 * Groups filtered emails by category and returns them as a keyed map.
 * Emails are stamped with `category_id` by the reducer before reaching here,
 * so no name→UUID rekeying is needed — the category field is already a UUID.
 */
export function buildEmailCategoryMap(
  filteredEmails: Email[],
  mode: InboxMode,
  _categorySummary: CategorySummaryItem[] | null | undefined
): Map<string, CategoryGroup> {
  const emailCategoryMap = new Map<string, CategoryGroup>();
  groupEmailsByCategory(filteredEmails, mode).forEach(group => {
    emailCategoryMap.set(group.category, group);
  });
  return emailCategoryMap;
}

/**
 * A "Other"-bucket proto-category group: the emails plus the proto's display
 * name and its ID. Carrying `id` lets the inbox promote / delete the proto by
 * ID directly, instead of re-deriving it from the (drift-prone) display name.
 */
export interface OtherProtoGroup {
  /** Proto-category ID, taken from the grouped emails. Null if none carry one. */
  id: string | null;
  name: string;
  emails: Email[];
}

export function buildOtherProtoGroups(emailCategoryMap: Map<string, CategoryGroup>): OtherProtoGroup[] {
  // After fix #1294: groupEmailsByCategory() now uses getCategoryKey(), so
  // emails with no category_id are keyed as CATEGORY_KEY_UNCATEGORIZED ("uncategorized")
  // instead of CATEGORY_OTHER ("Other"). Use the constant to keep keys in sync.
  const otherEmails = emailCategoryMap.get(CATEGORY_KEY_UNCATEGORIZED)?.emails ?? [];
  const groups = new Map<string, Email[]>();
  otherEmails.forEach(email => {
    const protoName = email.protoCategoryName;
    if (protoName) {
      if (!groups.has(protoName)) {
        groups.set(protoName, []);
      }
      groups.get(protoName)!.push(email);
    }
  });
  return Array.from(groups.entries()).map(([name, groupEmails]) => ({
    // Every email in the group shares the same proto; take the first non-empty ID.
    id: groupEmails.find(email => email.protoCategoryId)?.protoCategoryId ?? null,
    name,
    emails: groupEmails,
  }));
}

/**
 * Whether the "Other" accordion is currently expanded, in which case the inbox
 * should fetch the user's proto categories (needed to wire the per-group
 * "Convert to category" / delete actions to a real proto-category id).
 *
 * The "Other" category is keyed by `getCategoryKey`, which collapses the
 * name-"Other" sentinel to `CATEGORY_KEY_UNCATEGORIZED` ("uncategorized"), NOT
 * the literal "Other" string. `expandedCategories` holds those keys, so we must
 * look it up by the resolved key — checking for the raw "Other" name never
 * matches, which previously left proto categories unfetched and made the
 * "Convert to category" button a silent no-op (id resolved to '').
 */
export function shouldFetchProtoCategories(
  displayCategories: Array<{ id: string | null; name: string }>,
  expandedCategories: Set<string>
): boolean {
  const otherCategory = displayCategories.find(category => category.name === CATEGORY_OTHER);
  if (!otherCategory) {
    return false;
  }
  return expandedCategories.has(getCategoryKey(otherCategory.id, otherCategory.name));
}

/**
 * Categories to render as sections. A category whose reconciled count is 0 has no
 * rows to show and is left out (issue #2062), except when `includeEmpty` is set so
 * an admin in debug mode can still open its diagnostic panel.
 */
export function buildDisplayCategories(
  summaryCategories: CategorySummaryItem[] | null,
  filteredEmails: Email[],
  stableCategoryOrder: string[],
  mode: InboxMode,
  includeEmpty = false
): Array<{ id: string | null; name: string; count: number }> {
  const source: Array<{ id: string | null; name: string; count: number }> =
    summaryCategories ??
    groupEmailsByCategory(filteredEmails, mode).map(grp => ({
      id: null,
      name: grp.category,
      count: grp.emails.length,
    }));

  // Fix #1258: merge entries with duplicate display names (server-side dedup is
  // the primary fix; this is a defensive frontend layer that prevents duplicate
  // accordions if stale cached data slips through).
  const mergedByName = new Map<string, { id: string | null; name: string; count: number }>();
  for (const cat of source) {
    const existing = mergedByName.get(cat.name);
    if (existing) {
      // Combine counts; keep the first-seen UUID as canonical
      existing.count += cat.count;
    } else {
      mergedByName.set(cat.name, { ...cat });
    }
  }
  const mergedSource = Array.from(mergedByName.values());

  const nonEmptySource = includeEmpty ? mergedSource : mergedSource.filter(cat => cat.count > 0);
  if (stableCategoryOrder.length === 0) {
    return pinPendingCategoryFirst(nonEmptySource);
  }
  const orderMap = new Map(stableCategoryOrder.map((key, idx) => [key, idx]));
  const sorted = nonEmptySource.slice().sort((itemA, itemB) => {
    const keyA = getCategoryKey(itemA.id, itemA.name);
    const keyB = getCategoryKey(itemB.id, itemB.name);
    const orderA = orderMap.get(keyA) ?? Number.MAX_SAFE_INTEGER;
    const orderB = orderMap.get(keyB) ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
  return pinPendingCategoryFirst(sorted);
}

/** True for the "AI deciding category" summary entry (threads still being categorised). */
export function isPendingCategoryItem(category: { id?: string | null }): boolean {
  return category.id === CATEGORY_KEY_PENDING;
}

/** Whether the summary currently has threads waiting on categorisation. */
export function hasPendingCategorisation(categories: Array<{ id?: string | null; count: number }> | null | undefined): boolean {
  return (categories ?? []).some(category => isPendingCategoryItem(category) && category.count > 0);
}

/**
 * The pending bucket always renders first: it is where a new user's mail sits
 * until categorisation finishes, and its threads move out on their own. Pinning
 * it also keeps it from jumping around as the stable order is rebuilt.
 */
function pinPendingCategoryFirst<T extends { id: string | null }>(categories: T[]): T[] {
  const pendingIndex = categories.findIndex(isPendingCategoryItem);
  if (pendingIndex <= 0) {
    return categories;
  }
  const pending = categories[pendingIndex];
  return [pending, ...categories.slice(0, pendingIndex), ...categories.slice(pendingIndex + 1)];
}